import {useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {useNoticeLayer} from '@/hooks/common/noticeLayer/useNoticeLayer';
import {getCookie} from '@/utils/main';
import {
  getHiddenPopupState,
  setHiddenPopupState,
} from '@/utils/main/my-plus/bottomBanner';
import {ROUTE} from '@/constants/main/route';
import PromotionBannerContent from './PromotionBannerContent';
import {PromotionBannerProps} from '../type';

const COOKIE_KEY_HIDE_BANNER = 'chk_popup';
const ALLOWED_PATHS = [ROUTE.INDEX];

// useNoticeLayer이 제공하는 로직을 사용하지 못해 이 훅을 사용하는 이점이 없어보이나(쿠키 기반 노출 로직, defaultUi)
// 추후 방송국 쿠키도 비슷한 로직으로 바뀔 수 있다고 하여 유지...
const PromotionBanner = ({bannerList, sendClickLog}: PromotionBannerProps) => {
  const [showIndex, setShowIndex] = useState<number>(0);
  const {showNoticeLayer, closeNoticeLayer} = useNoticeLayer({
    // 임의값. useNoticeLayer의 쿠키 기반 노출 로직을 사용하지 않고, targetLayers에서 처리하고 있음
    id: 'unused_hide_promotion_banner',
    hideDefaultUi: true,
    allowPaths: ALLOWED_PATHS,
  });

  // 노출 조건 만족하는 '모든' 배너 찾기
  // ? 배너가 중첩으로 노출될 예정이라고 했었는데 다시 단일 노출이라고 한다(그래서 qa 안된상태)
  // 단일 노출에 이상이 없어서 현재 로직 유지
  const targetLayers = useMemo(() => {
    // 쿠키에서 숨겨진 ID 목록 가져오기
    const hiddenStates = getHiddenPopupState(COOKIE_KEY_HIDE_BANNER);
    const today = dayjs();

    return bannerList.filter(({bannerName, startDate, endDate}) => {
      const isNotStarted = today.isBefore(dayjs(startDate));
      const isExpired = today.isAfter(dayjs(endDate));
      const isHiddenByCookie = hiddenStates[bannerName] === 'y';

      const shouldShowLayer = !isNotStarted && !isExpired && !isHiddenByCookie;
      return shouldShowLayer;
    });
  }, [bannerList]);

  useEffect(() => {
    const data = targetLayers[showIndex];
    if (!data) {
      return;
    }

    // 어드민 배너 과도기 예외처리
    // 251110 이후 이 커밋변경내역을 전부 지워주세요!
    if (data.bannerName === 'AI 영상 콘테스트') {
      const COOKIE_KEY_HIDE_SAVYG = 'main-popup-savyg-contest-layer';
      const isHiddenSavyg = getCookie(COOKIE_KEY_HIDE_SAVYG) === 'y';
      if (isHiddenSavyg) {
        setShowIndex((prev) => prev + 1);
        return;
      }
    }

    const handleLinkClick = () => {
      sendClickLog('landing_page');
    };

    // 닫기
    const handleClose = () => {
      closeNoticeLayer();
      setShowIndex((prev) => prev + 1);
      sendClickLog('close');
    };

    // 다시 보지 않기
    const handleHide = () => {
      setHiddenPopupState(COOKIE_KEY_HIDE_BANNER, data.bannerName, 1);
      closeNoticeLayer();
      setShowIndex((prev) => prev + 1);
      sendClickLog('do_not_show');
    };

    showNoticeLayer({
      content: () => (
        <PromotionBannerContent
          {...data}
          handleLinkClick={handleLinkClick}
          handleClose={handleClose}
          handleHide={handleHide}
        />
      ),
      startDate: data.startDate,
      endDate: data.endDate,
    });

    return () => {
      closeNoticeLayer();
    };
  }, [
    showNoticeLayer,
    closeNoticeLayer,
    sendClickLog,
    targetLayers,
    showIndex,
  ]);

  return null;
};

export default PromotionBanner;
